import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import AdvancedImageLoading from "../../../../components/TailoredGeneration/SharedPanels/AdvancedImageLoading/AdvancedImageLoading";
import AdvancedImageLoadingCompleted from "../../../../components/TailoredGeneration/SharedPanels/AdvancedImageLoadingCompleted/AdvancedImageLoadingCompleted";
import AdvancedImageLoadingError from "../../../../components/TailoredGeneration/SharedPanels/AdvancedImageLoadingError/AdvancedImageLoadingError";
import BestPractisesButton from "../../../../components/common/BestPractisesButton/BestPractisesButton.tsx";
import RouterConstants from "../../../../constants/RouterConstants.ts";
import useSecureNavigate from "../../../../hooks/useSecureNavigate.tsx";
import { useAppStore } from "../../../../hooks/useStores";
import EditorLayout from "../../../../layout/EditorLayout/EditorLayout";
import iframeStore from "../../../IframeNew/iframe-store.tsx";
import { BulkUploadStatus, TgDataset, defaultDataset } from "../../Datasets/store/tg-dataset-store.tsx";
import SelectTgDatasetStep from "../Create/Dataset/SelectTgDatasetStep.tsx";
import { CreateTgModelPanels, TrainingMode, defaultModel } from "../store/tg-model-store.tsx";
import styles from "./FiboCreateTgModel.module.scss";
import NewCreateTgModelDataset from "./NewCreateTgModelDataset/NewCreateTgModelDataset";
import NewTgAdvancedUpload from "./NewTgAdvancedUpload/NewTgAdvancedUpload.tsx";
import NewTgExpertParametersSetup from "./NewTgExpertParametersSetup/NewTgExpertParametersSetup.tsx";
import NewTgUploadMethod from "./NewTgUploadMethod/NewTgUploadMethod";
import TgDatasetCaptioning from "./TgDatasetCaptioning/TgDatasetCaptioning.tsx";
import TgImagesCaptioning from "./TgImagesCaptioning/TgImagesCaptioning.tsx";
import { TrainingSetupSummary } from "./components/TrainingSetupSummary/TrainingSetupSummary.tsx";

const FiboCreateTgModel = () => {
	const { t } = useTranslation("tg", { keyPrefix: "models.create" });
	const { tgStore } = useAppStore();
	const { tgModelStore, tgDatasetStore } = tgStore;
	const [currentPanel, setCurrentPanel] = useState<CreateTgModelPanels>(CreateTgModelPanels.SELECT_DATASET_PANEL);
	const [initialStatus, setInitialStatus] = useState<BulkUploadStatus | null>(null);
	const [lastStatus, setLastStatus] = useState<BulkUploadStatus | null>(null);
	const [isVisitedImagesCaptioning, setIsVisitedImagesCaptioning] = useState<boolean>(false);
	const navigate = useSecureNavigate();
	const { projectId } = useParams();

	useEffect(() => {
		tgDatasetStore.datasetForm.training_version = "fibo";
		tgModelStore.modelForm.training_version = "fibo";

		return () => {
			if (
				!tgModelStore.isDatasetFromExisting &&
				tgDatasetStore.datasetForm.status === "Draft" &&
				tgDatasetStore.datasetForm.id
			) {
				tgDatasetStore.deleteDataset(tgDatasetStore.datasetForm.id);
			}
			tgDatasetStore.fetchVisualSchemaOnMount = true;
			tgModelStore.isDatasetFromExisting = false;
			tgDatasetStore.datasetForm = defaultDataset;
			tgDatasetStore.datasetForm.training_version = "fibo";
			tgModelStore.modelForm = defaultModel;
			tgModelStore.modelForm.training_version = "fibo" as any;
			tgDatasetStore.isFormDuplicateTriggered = false;
			window.history.replaceState({}, "");
			tgDatasetStore.csvUploadErrors = [];
			tgDatasetStore.csvUploadWarnings = [];
			tgDatasetStore.imagesZipUrl = undefined;
		};
	}, []);

	useEffect(() => {
		if (iframeStore.isIframe()) {
			const enabledModels = iframeStore.iframe?.config.tg_training_config?.enabled_training_models;
			if (enabledModels && !enabledModels.expert && enabledModels.fully_automated) {
				handleTrainingModeSelect(TrainingMode.FULLY_AUTOMATED);
			}
		}
	}, [iframeStore.iframe?.config.tg_training_config?.enabled_training_models]);

	const getSecondaryButton = () => {
		return <BestPractisesButton fontSize="small" hideIcon buttonType={"secondarySmall"} />;
	};

	const handleTrainingModeSelect = (mode: TrainingMode) => {
		tgModelStore.modelForm.training_mode = mode;

		if (tgModelStore.modelForm.training_mode === TrainingMode.FULLY_AUTOMATED) {
			setCurrentPanel(CreateTgModelPanels.TRAINING_VERSION_PANEL);
		} else if (tgModelStore.modelForm.training_mode === TrainingMode.EXPERT) {
			setCurrentPanel(CreateTgModelPanels.SELECT_DATASET_PANEL);
		}
	};

	const handleSelectDataset = (isSelectExisting: boolean = false) => {
		if (isSelectExisting) {
			setCurrentPanel(CreateTgModelPanels.PARAMETERS_SETUP_PANEL);
		} else {
			setCurrentPanel(CreateTgModelPanels.UPLOAD_METHOD_PANEL);
		}
	};

	const handleUploadMethodNext = () => {
		setCurrentPanel(CreateTgModelPanels.CREATE_MODEL_DATASET_PANEL);
	};

	const handleUseAdvancedNext = () => {
		setCurrentPanel(CreateTgModelPanels.ADVANCED_UPLOAD_PANEL);
	};

	const startTraining = async (navigateAfter: boolean) => {
		const completedDatasetForTraining: TgDataset = {
			...tgDatasetStore.datasetForm,
			name: tgModelStore.modelForm.name,
			status: "Completed",
		};
		tgModelStore.modelForm.dataset_id = tgDatasetStore.datasetForm.id;
		const [_updatedDataset, newModel] = await Promise.all([
			tgDatasetStore.updateDataset(tgDatasetStore.datasetForm.id, completedDatasetForTraining),
			tgModelStore.createModel(tgModelStore.modelForm),
		]);
		newModel.training_parameters = tgModelStore.modelForm.training_parameters;
		await tgModelStore.startTraining(newModel);
		tgDatasetStore.datasetForm = completedDatasetForTraining;
		if (navigateAfter) {
			navigate(`${RouterConstants.NEW_TG.PROJECTS.fullPath}/${projectId}/${RouterConstants.NEW_TG.MODELS.path}`);
		}
	};

	const handleAdvancedImageUploadComplete = (firstStatus: BulkUploadStatus) => {
		setInitialStatus(firstStatus);
		setCurrentPanel(CreateTgModelPanels.ADVANCED_UPLOAD_LOADING_PANEL);
	};

	const handleAdvancedImageUploadCancel = () => {
		tgDatasetStore.datasetForm.id = NaN;
		setInitialStatus(null);
		setCurrentPanel(CreateTgModelPanels.ADVANCED_UPLOAD_PANEL);
	};

	const handleAdvancedImageUploadLoadingComplete = (status: BulkUploadStatus) => {
		setLastStatus(status);
		setCurrentPanel(CreateTgModelPanels.ADVANCED_UPLOAD_COMPLETED_PANEL);
	};

	const goToParametersFromCaptioning = () => {
		if (tgDatasetStore.datasetForm.id) {
			tgDatasetStore.updateDataset(tgDatasetStore.datasetForm.id, tgDatasetStore.datasetForm).catch(() => {});
		}
		setCurrentPanel(CreateTgModelPanels.PARAMETERS_SETUP_PANEL);
	};

	const onReviewCaptions = () => {
		if (tgDatasetStore.datasetForm?.id) {
			tgDatasetStore
				.regenerateAllCaptions(
					tgDatasetStore.datasetForm.id,
					undefined,
					(newDataset: TgDataset) => {
						tgDatasetStore.handleFormChange("images", newDataset.images);
						tgDatasetStore.handleFormChange(
							"captions_update_status" as any,
							newDataset.captions_update_status as any,
						);
					},
					300,
					20,
				)
				.catch(() => {});
		}
		setIsVisitedImagesCaptioning(true);
		setCurrentPanel(CreateTgModelPanels.IMAGES_CAPTIONING_PANEL);
	};

	const title = tgModelStore.modelForm?.name || t("title");
	return (
		<>
			<EditorLayout
				className={
					currentPanel === CreateTgModelPanels.PARAMETERS_SETUP_PANEL
						? styles.editorContainerScroll
						: undefined
				}
				childrenClassName={`${styles.wrapper} ${
					currentPanel === CreateTgModelPanels.PARAMETERS_SETUP_PANEL ? styles.childrenOverflowVisible : ""
				}`}
				primaryButton={
					currentPanel === CreateTgModelPanels.PARAMETERS_SETUP_PANEL ? <TrainingSetupSummary /> : undefined
				}
				name={
					currentPanel === CreateTgModelPanels.UPLOAD_METHOD_PANEL ||
					currentPanel === CreateTgModelPanels.CREATE_MODEL_DATASET_PANEL ||
					currentPanel === CreateTgModelPanels.ADVANCED_UPLOAD_PANEL
						? `${t("datasetUploadTitle") ? `${title} - ${t("datasetUploadTitle")}` : title}`
						: currentPanel === CreateTgModelPanels.DATASET_CAPTIONING_PANEL ||
							  currentPanel === CreateTgModelPanels.IMAGES_CAPTIONING_PANEL
							? `${title} - ${t("captioning.pageTitle")}`
							: currentPanel === CreateTgModelPanels.PARAMETERS_SETUP_PANEL
								? `${title} - ${t("trainingSetupTitle")}`
								: title
				}
				secondaryButton={
					currentPanel === CreateTgModelPanels.CREATE_MODEL_DATASET_PANEL ? getSecondaryButton() : undefined
				}
			>
				{currentPanel === CreateTgModelPanels.SELECT_DATASET_PANEL ? (
					<SelectTgDatasetStep
						handleSkip={() => handleSelectDataset(true)}
						handleSelectDataset={handleSelectDataset}
					/>
				) : currentPanel === CreateTgModelPanels.UPLOAD_METHOD_PANEL ? (
					<NewTgUploadMethod
						handleUploadImagesNext={handleUploadMethodNext}
						handleUseAdvancedNext={handleUseAdvancedNext}
					/>
				) : currentPanel === CreateTgModelPanels.ADVANCED_UPLOAD_PANEL ? (
					<NewTgAdvancedUpload
						onSwitchToBasicDataset={() => {
							tgDatasetStore.datasetForm = defaultDataset;
							setCurrentPanel(CreateTgModelPanels.UPLOAD_METHOD_PANEL);
						}}
						onSetVisualSchema={() => setCurrentPanel(CreateTgModelPanels.DATASET_CAPTIONING_PANEL)}
					/>
				) : currentPanel === CreateTgModelPanels.ADVANCED_UPLOAD_LOADING_PANEL ? (
					<AdvancedImageLoading
						datasetId={tgDatasetStore.datasetForm.id}
						onCancel={handleAdvancedImageUploadCancel}
						onCompleted={handleAdvancedImageUploadLoadingComplete}
						onError={() => setCurrentPanel(CreateTgModelPanels.ADVANCED_UPLOAD_ERROR_PANEL)}
						initialStatus={initialStatus ?? undefined}
					/>
				) : currentPanel === CreateTgModelPanels.ADVANCED_UPLOAD_COMPLETED_PANEL ? (
					<AdvancedImageLoadingCompleted
						status={lastStatus as BulkUploadStatus}
						handleContinueClick={() => setCurrentPanel(CreateTgModelPanels.PARAMETERS_SETUP_PANEL)}
					/>
				) : currentPanel === CreateTgModelPanels.ADVANCED_UPLOAD_ERROR_PANEL ? (
					<AdvancedImageLoadingError
						onTryAgain={() => setCurrentPanel(CreateTgModelPanels.ADVANCED_UPLOAD_PANEL)}
					/>
				) : currentPanel === CreateTgModelPanels.PARAMETERS_SETUP_PANEL ? (
					<NewTgExpertParametersSetup
						isVisitedImagesCaptioning={isVisitedImagesCaptioning}
						onBackToVisualDna={
							!isVisitedImagesCaptioning
								? () => {
										tgDatasetStore.fetchVisualSchemaOnMount = false;
										setCurrentPanel(CreateTgModelPanels.DATASET_CAPTIONING_PANEL);
									}
								: undefined
						}
						handleCreateModelAndStartTraining={() => startTraining(true)}
						handleStartTrainingOnly={() => startTraining(false)}
						handleNavigateAfterTraining={() =>
							navigate(
								`${RouterConstants.NEW_TG.PROJECTS.fullPath}/${projectId}/${RouterConstants.NEW_TG.MODELS.path}`,
							)
						}
						loading={
							tgModelStore.loadingStartTraining ||
							tgModelStore.loadingCreateModel ||
							tgDatasetStore.loadingUpdateDataset
						}
						datasetSize={lastStatus?.total_processed ?? tgDatasetStore.datasetForm.images_count}
					/>
				) : currentPanel === CreateTgModelPanels.CREATE_MODEL_DATASET_PANEL ? (
					<NewCreateTgModelDataset
						onContinue={() => setCurrentPanel(CreateTgModelPanels.DATASET_CAPTIONING_PANEL)}
					/>
				) : currentPanel === CreateTgModelPanels.DATASET_CAPTIONING_PANEL ? (
					<TgDatasetCaptioning
						onTrainModel={() => goToParametersFromCaptioning()}
						onStartAdvancedUpload={handleAdvancedImageUploadComplete}
						onReviewCaptions={onReviewCaptions}
						onBackToUpload={
							!isVisitedImagesCaptioning
								? () => setCurrentPanel(CreateTgModelPanels.CREATE_MODEL_DATASET_PANEL)
								: undefined
						}
					/>
				) : currentPanel === CreateTgModelPanels.IMAGES_CAPTIONING_PANEL ? (
					<TgImagesCaptioning onTrainModel={() => goToParametersFromCaptioning()} />
				) : null}
			</EditorLayout>
		</>
	);
};

export default observer(FiboCreateTgModel);
